import React, { ComponentProps, memo, ReactNode } from 'react'

import styled from '@emotion/styled'

import {
  absoluteCenter,
  Box,
  Button,
  RemoveButton,
  FlexBox,
  Loader,
} from '@surglogs-app/ui'

interface SignatureViewerProps {
  value: string | null | undefined
  uploading?: boolean
}

const SignatureImage = styled.span<{ url: string }>`
  width: 100%;
  background: ${({ url }) => `url(${url}) no-repeat center center`};
  height: 100%;
  min-width: 250px;
  display: block;
  background-size: auto;
`

const SignatureContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  border: 1px solid;
  border-color: ${({ theme }) => theme.ui.colors['navy.25']};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  overflow: hidden;
`

const StyledRemoveButton = styled(RemoveButton)`
  height: 100%;
  padding: 0;
  margin-left: 10px;
  width: 50px;
  position: relative;
  & span {
    ${absoluteCenter};
  }
`

const SignatureViewer = memo(
  ({ value, uploading, ...rest }: SignatureViewerProps) => (
    <SignatureContainer {...rest}>
      {value ? (
        <SignatureImage
          {...{ url: value, role: 'img', 'aria-label': 'signature-image' }}
        />
      ) : null}
      {uploading ? <Loader /> : null}
    </SignatureContainer>
  ),
)

const Container = styled(FlexBox)`
  flex: 1;
  align-items: center;
  height: 180px;
`

interface SignatureInputProps {
  value: string | null | undefined
  uploading?: boolean
  editable?: boolean
  addButtonProps: ComponentProps<typeof Button>
  removeButtonProps: ComponentProps<typeof RemoveButton>
  warning?: string | boolean | ReactNode
}

const AddButton = styled(Button)`
  width: 100%;
`

export const SignatureInput = memo(
  ({
    value,
    uploading,
    editable,
    removeButtonProps,
    addButtonProps,
    warning,
    ...rest
  }: SignatureInputProps) =>
    value || uploading ? (
      <Container {...{ role: 'group', 'aria-label': 'signature' }}>
        <SignatureViewer {...{ value, uploading }} {...rest} />
        {editable ? (
          <StyledRemoveButton
            {...{ 'aria-label': 'remove-signature' }}
            {...removeButtonProps}
          />
        ) : null}
      </Container>
    ) : (
      <AddButton
        {...addButtonProps}
        {...{ variant: warning ? 'warning' : 'default' }}
      />
    ),
)
